import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { Box, LinearProgress } from '@mui/material';
import { Carwash, MapBounds } from '../../types/CarwashType';
import { GeolocationCoords } from '../../hooks/useGeolocation';
import PartnerInfoWindow from './PartnerInfoWindow';

const MAP_CONTAINER_STYLE: React.CSSProperties = { width: '100%', height: '100%' };
const DEFAULT_ZOOM = 13;

const GOOGLE_MAPS_LIBRARIES: ('places' | 'marker')[] = ['places'];

const MAP_OPTIONS: google.maps.MapOptions = {
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  clickableIcons: false,
  styles: [
    { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  ],
};

function buildNormalIcon(): google.maps.Symbol {
  return {
    path: google.maps.SymbolPath.CIRCLE,
    scale: 8,
    fillColor: '#00b4e4',
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 2,
  };
}

function buildSelectedIcon(): google.maps.Symbol {
  return {
    path: google.maps.SymbolPath.CIRCLE,
    scale: 11,
    fillColor: '#0077a8',
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 2.5,
  };
}

function buildUserIcon(): google.maps.Symbol {
  return {
    path: google.maps.SymbolPath.CIRCLE,
    scale: 10,
    fillColor: '#4285F4',
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 3,
  };
}

export interface PartnerMapHandle {
  panToCity: (city: string, state: string) => Promise<boolean>;
}

interface Props {
  carwashes: Carwash[];
  userCoords: GeolocationCoords | null;
  center: GeolocationCoords;
  selectedCarwashId: number | null;
  onBoundsChange: (bounds: MapBounds) => void;
  onCarwashSelect: (carwash: Carwash | null) => void;
}

const PartnerMap = forwardRef<PartnerMapHandle, Props>(function PartnerMap(
  { carwashes, userCoords, center, selectedCarwashId, onBoundsChange, onCarwashSelect },
  ref
) {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const markersMapRef = useRef<Map<number, google.maps.Marker>>(new Map());
  const prevSelectedIdRef = useRef<number | null>(null);
  const isPanningRef = useRef(false);
  const [mapReady, setMapReady] = useState(false);

  const onCarwashSelectRef = useRef(onCarwashSelect);
  useEffect(() => { onCarwashSelectRef.current = onCarwashSelect; }, [onCarwashSelect]);

  useImperativeHandle(ref, () => ({
    panToCity: async (city, state) => {
      if (!mapRef.current || !isLoaded) return false;
      try {
        const geocoder = new google.maps.Geocoder();
        const query = [city, state, 'Brasil'].filter(Boolean).join(', ');
        const result = await geocoder.geocode({ address: query });
        if (!result.results.length) return false;
        const viewport = result.results[0].geometry.viewport;
        mapRef.current.fitBounds(viewport);
        return true;
      } catch {
        return false;
      }
    },
  }), [isLoaded]);

  const handleMapClick = useCallback(() => {
    onCarwashSelectRef.current(null);
  }, []);

  const handleLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMapReady(true);
  }, []);

  const handleUnmount = useCallback(() => {
    markersMapRef.current.forEach((m) => m.setMap(null));
    markersMapRef.current.clear();
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current = null;
    }
    mapRef.current = null;
    setMapReady(false);
  }, []);

  const handleIdle = useCallback(() => {
    if (!mapRef.current) return;
    if (isPanningRef.current) {
      isPanningRef.current = false;
      return;
    }
    const bounds = mapRef.current.getBounds();
    if (!bounds) return;
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    onBoundsChange({
      ne_lat: ne.lat(),
      ne_lng: ne.lng(),
      sw_lat: sw.lat(),
      sw_lng: sw.lng(),
    });
  }, [onBoundsChange]);

  useEffect(() => {
    if (!isLoaded || !mapReady || !mapRef.current) return;

    markersMapRef.current.forEach((m) => m.setMap(null));
    markersMapRef.current.clear();
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
    }

    if (carwashes.length === 0) return;

    const normalIcon = buildNormalIcon();
    const newMarkers: google.maps.Marker[] = [];

    carwashes.forEach((carwash) => {
      const marker = new google.maps.Marker({
        position: { lat: carwash.latitude, lng: carwash.longitude },
        title: carwash.car_wash_name,
        icon: normalIcon,
        zIndex: 1,
        map: mapRef.current!,
      });
      marker.addListener('click', () => onCarwashSelectRef.current(carwash));
      markersMapRef.current.set(carwash.id, marker);
      newMarkers.push(marker);
    });

    if (!clustererRef.current) {
      clustererRef.current = new MarkerClusterer({ map: mapRef.current, markers: newMarkers });
    } else {
      clustererRef.current.addMarkers(newMarkers);
    }

    prevSelectedIdRef.current = null;
  }, [carwashes, isLoaded, mapReady]);

  useEffect(() => {
    const prevId = prevSelectedIdRef.current;

    if (prevId !== null && prevId !== selectedCarwashId) {
      const prev = markersMapRef.current.get(prevId);
      if (prev) {
        prev.setIcon(buildNormalIcon());
        prev.setZIndex(1);
      }
    }

    if (selectedCarwashId !== null) {
      const next = markersMapRef.current.get(selectedCarwashId);
      if (next) {
        next.setIcon(buildSelectedIcon());
        next.setZIndex(10);
      }
    }

    prevSelectedIdRef.current = selectedCarwashId;
  }, [selectedCarwashId]);

  useEffect(() => {
    if (!mapRef.current || selectedCarwashId === null) return;
    const carwash = carwashes.find((c) => c.id === selectedCarwashId);
    if (carwash) {
      isPanningRef.current = true;
      mapRef.current.panTo({ lat: carwash.latitude, lng: carwash.longitude });
    }
  }, [selectedCarwashId, carwashes]);

  const selectedCarwash = useMemo(
    () => (selectedCarwashId !== null ? carwashes.find((c) => c.id === selectedCarwashId) ?? null : null),
    [selectedCarwashId, carwashes]
  );

  const mapCenter = useMemo(
    () => ({ lat: center.latitude, lng: center.longitude }),
    [center.latitude, center.longitude]
  );

  if (!isLoaded) {
    return (
      <Box sx={{ width: '100%', height: '100%', bgcolor: '#e8eaed', position: 'relative' }}>
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            bgcolor: 'rgba(0,180,228,0.15)',
            '& .MuiLinearProgress-bar': { bgcolor: '#00b4e4' },
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={mapCenter}
        zoom={DEFAULT_ZOOM}
        options={MAP_OPTIONS}
        onLoad={handleLoad}
        onUnmount={handleUnmount}
        onIdle={handleIdle}
        onClick={handleMapClick}
      >
        {userCoords && (
          <Marker
            position={{ lat: userCoords.latitude, lng: userCoords.longitude }}
            icon={buildUserIcon()}
            title="Sua localização"
            zIndex={999}
          />
        )}
      </GoogleMap>

      {selectedCarwash && (
        <PartnerInfoWindow
          carwash={selectedCarwash}
          onClose={handleMapClick}
        />
      )}
    </Box>
  );
});

export default PartnerMap;
